/**
 * API client for the NovaHES console.
 *
 * Every request goes through one function so that authentication, error
 * shaping and session expiry are handled in a single place. The legacy
 * console pattern of calling fetch directly from each screen is how a
 * component ends up silently swallowing a 401 and rendering an empty table
 * that looks like "no devices" rather than "you are signed out".
 */

const TOKEN_KEY = "novahes.session";
const SCOPE_KEY = "novahes.scope";

/**
 * The tenant the operator is currently acting within.
 *
 * Staff can see every tenant on the platform, so nothing they do on the
 * operational surface is meaningful until they say which estate they mean.
 * The choice is sent on each request rather than held on the server, so two
 * browser tabs can look at two different tenants without one silently
 * redirecting the other's next command.
 */
export function storedScope() {
  return localStorage.getItem(SCOPE_KEY) || "";
}

export function storeScope(alias) {
  if (alias) localStorage.setItem(SCOPE_KEY, alias);
  else localStorage.removeItem(SCOPE_KEY);
}

export function storedSession() {
  try {
    const raw = localStorage.getItem(TOKEN_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function storeSession(session) {
  localStorage.setItem(TOKEN_KEY, JSON.stringify(session));
}

export function clearSession() {
  localStorage.removeItem(TOKEN_KEY);
}

/** ApiError carries the machine-readable code the server returned. */
export class ApiError extends Error {
  constructor(status, code, message) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

/** ScopeRequiredError is raised before a request that has no tenant to act in. */
export class ScopeRequiredError extends ApiError {
  constructor() {
    super(0, "scope_required", "Choose a tenant before using this screen.");
  }
}

let onUnauthenticated = () => {};

/** Registers the callback invoked when the server rejects our credentials. */
export function setUnauthenticatedHandler(fn) {
  onUnauthenticated = fn;
}

// Paths under /api are the operational surface and are always scoped to one
// tenant. Everything else is platform administration and is not.
function needsScope(path) {
  return path.startsWith("/api/");
}

async function request(method, path, body, options = {}) {
  const session = storedSession();
  const headers = { Accept: "application/json" };

  if (session?.token && !options.anonymous) {
    headers.Authorization = `Bearer ${session.token}`;
  }
  if (needsScope(path)) {
    const scope = storedScope();
    if (!scope) {
      // Failing here rather than sending the request keeps the console from
      // reporting "unauthorised" for what is really an unmade choice.
      throw new ScopeRequiredError();
    }
    headers["X-MeshNova-Tenant"] = scope;
  }
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
  }

  let response;
  try {
    response = await fetch(path, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (cause) {
    // A network failure and a server error are different problems with
    // different remedies, so they are not collapsed into one message.
    throw new ApiError(0, "network_unreachable",
      "Could not reach the head-end server. Check that it is running.");
  }

  if (response.status === 401 && !options.anonymous) {
    clearSession();
    onUnauthenticated();
    throw new ApiError(401, "session_expired", "Your session has expired. Please sign in again.");
  }

  if (response.status === 204) return null;

  let payload = null;
  const text = await response.text();
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      throw new ApiError(response.status, "bad_response",
        "The server returned a response the console could not read.");
    }
  }

  if (!response.ok) {
    const err = payload?.error;
    throw new ApiError(
      response.status,
      err?.code ?? "request_failed",
      err?.message ?? `Request failed with status ${response.status}.`
    );
  }

  return payload?.data ?? null;
}

const api = {
  authConfig: () => request("GET", "/auth/config", undefined, { anonymous: true }),
  get: (path) => request("GET", path),
  post: (path, body) => request("POST", path, body ?? {}),
  patch: (path, body) => request("PATCH", path, body ?? {}),
  del: (path) => request("DELETE", path),

  // ---- authentication ----
  //
  // Signing in takes two calls. The first checks the password and sends a
  // six-digit code to the operator's address; the second exchanges that code
  // for a session. Nothing usable comes back from the first, so a stolen
  // password on its own gets no further than the code screen.
  beginLogin: (email, password) =>
    request("POST", "/auth/login", { email, password }, { anonymous: true }),
  verifyLogin: (challengeId, code) =>
    request("POST", "/auth/verify", { challenge_id: challengeId, code }, { anonymous: true }),
  // Recovery for somebody who cannot sign in at all, so it carries no
  // credential. The server answers identically for an address it does not
  // know, which is why there is nothing here to branch on.
  forgotPassword: (email) =>
    request("POST", "/auth/forgot-password", { email }, { anonymous: true }),
  resetPasswordWithCode: (challengeId, code, newPassword) =>
    request(
      "POST",
      "/auth/reset-password",
      { challenge_id: challengeId, code, new_password: newPassword },
      { anonymous: true }
    ),
  beginEmailChange:(password,email)=>request("POST","/auth/email/start",{password,email}),
  completeEmailChange:(challenge_id,code)=>request("POST","/auth/email/complete",{challenge_id,code}),
  logout: () => request("POST", "/auth/logout", {}),
  whoami: () => request("GET", "/auth/whoami"),
  changePassword: (currentPassword, newPassword) =>
    request("POST", "/auth/change-password", {
      current_password: currentPassword,
      new_password: newPassword,
    }),

  // ---- operators ----
  listOperators: () => request("GET", "/admin/operators"),
  createOperator: (body) => request("POST", "/admin/operators", body),
  resetOperatorPassword: (email, newPassword) =>
    request("POST", `/admin/operators/${encodeURIComponent(email)}/reset-password`, {
      new_password: newPassword,
    }),
  setOperatorActive: (email, active) =>
    request("POST", `/admin/operators/${encodeURIComponent(email)}/${active ? "activate" : "deactivate"}`, {}),

  // ---- tenants ----
  listTenants: () => request("GET", "/admin/tenants"),
  getTenant: (alias) => request("GET", `/admin/tenants/${encodeURIComponent(alias)}`),
  createTenant: (body) => request("POST", "/admin/tenants", body),
  updateTenant: (alias, body) => request("PATCH", `/admin/tenants/${encodeURIComponent(alias)}`, body),
  setTenantActive: (alias, active) =>
    request("POST", `/admin/tenants/${encodeURIComponent(alias)}/${active ? "activate" : "deactivate"}`, {}),
  rotateTenantSecret: (alias) =>
    request("POST", `/admin/tenants/${encodeURIComponent(alias)}/rotate-secret`, {}),

  // ---- models and devices ----
  listModels: () => request("GET", "/admin/models"),
  createModel: (body) => request("POST", "/admin/models", body),
  registerDevice: (body) => request("POST", "/admin/devices", body),
  registerBatch: (body) => request("POST", "/admin/devices/batch", body),
  issueDevice: (number, tenantAlias) =>
    request("POST", `/admin/devices/${encodeURIComponent(number)}/issue`, { tenant_alias: tenantAlias }),
  issueBatch: (body) => request("POST", "/admin/devices/issue-batch", body),
  transitionBatch: (body) => request("POST", "/admin/devices/state-batch", body),

  // ---- tenant-scoped surface ----
  listDevices: (params) => request("GET", `/api/devices${query(params)}`),
  getDevice: (number) => request("GET", `/api/devices/${encodeURIComponent(number)}`),
  deviceChannels: (number) => request("GET", `/api/devices/${encodeURIComponent(number)}/channels`),
  deviceChildren: (number) => request("GET", `/api/devices/${encodeURIComponent(number)}/children`),
  attachDevice: (number, gateway) =>
    request("POST", `/api/devices/${encodeURIComponent(number)}/attach`, { gateway }),
  detachDevice: (number) => request("POST", `/api/devices/${encodeURIComponent(number)}/detach`, {}),
  // The supply path is a different relationship from the gateway path: it
  // answers who loses power when a device fails, not who carries its traffic.
  deviceSupplied: (number) => request("GET", `/api/devices/${encodeURIComponent(number)}/supplied`),
  supplyDevice: (number, supply) =>
    request("POST", `/api/devices/${encodeURIComponent(number)}/supply`, { supply }),
  unsupplyDevice: (number) =>
    request("POST", `/api/devices/${encodeURIComponent(number)}/unsupply`, {}),
  transitionDevice: (number, state) =>
    request("POST", `/api/devices/${encodeURIComponent(number)}/state`, { state }),
  setPowerLimit: (number, watts) =>
    request("POST", `/api/devices/${encodeURIComponent(number)}/power-limit`, { watts }),
  rotateDeviceKey: (number) =>
    request("POST", `/api/devices/${encodeURIComponent(number)}/rotate-key`, {}),
  assignChannelCustomer: (number, customerRef) =>
    request("POST", `/api/channels/${encodeURIComponent(number)}/customer`, { customer_ref: customerRef }),

  // ---- commands ----
  commandCatalog: () => request("GET", "/api/commands/catalog"),
  sendCommand: (number, body) =>
    request("POST", `/api/devices/${encodeURIComponent(number)}/commands`, body),
  deviceCommands: (number, limit = 50) =>
    request("GET", `/api/devices/${encodeURIComponent(number)}/commands?limit=${limit}`),
  getTransaction: (id) => request("GET", `/api/transactions/${encodeURIComponent(id)}`),

  // ---- vending ----
  tokenActions: () => request("GET", "/api/tokens/actions"),
  vend: (body) => request("POST", "/api/vend", body),
  vendHistory: (number, limit = 50) =>
    request("GET", `/api/devices/${encodeURIComponent(number)}/vends?limit=${limit}`),

  // ---- telemetry and analytics ----
  alerts: (params) => request("GET", `/api/alerts${query(params)}`),
  latestReading: (number, channel = 0) =>
    request("GET", `/api/devices/${encodeURIComponent(number)}/readings/latest?channel=${channel}`),
  deviceUsage: (number, params) =>
    request("GET", `/api/devices/${encodeURIComponent(number)}/usage${query(params)}`),
  tenantUsage: (params) => request("GET", `/api/usage${query(params)}`),
  topConsumers: (params) => request("GET", `/api/usage/top${query(params)}`),
  rebuildRollups: (params) => request("POST", `/admin/analytics/rebuild${query(params)}`, {}),

  // ---- service principal ----
  tenantDirectory: () => request("GET", "/service/tenants"),
};

function query(params) {
  if (!params) return "";
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") {
      search.set(key, value);
    }
  }
  const s = search.toString();
  return s ? `?${s}` : "";
}

export default api;
